<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import * as zod from 'zod'

import { usePlayerSettings } from '@/stores/playerSettings'
import { useGameState } from '@/stores/gameState'
import { useUiState } from '@/stores/uiState'

import UiPanel from '@/components/shared/UiPanel.vue'
import UiAvatarSelector from '@/components/shared/UiAvatarSelector.vue'
import UiButton from '@/components/shared/UiButton.vue'
import UiInput from '@/components/shared/UiInput.vue'
import ModalWrapper from './ModalWrapper.vue'
import PlayerIcon from '@/assets/icons/player.svg?component'

import { validate } from '@/helpers/validation'

import type { GameConfig, SitDownAction } from '@acepoker/contract/acepoker'

// in BB
const STACK_LIMITS = {
  default: 100,
  min: 50,
  max: 500
}

defineProps<{
  title: string
  welcomeText: string
  actionText: string
}>()

const emit = defineEmits<{
  join: [Omit<SitDownAction, 'action'>]
}>()

const { t } = useI18n()
const { nickname, avatarId } = storeToRefs(usePlayerSettings())
const { roomState } = storeToRefs(useGameState())
const { sitDown } = useGameState()
const uiState = useUiState()

const biggestBlind = ref(
  roomState.value.gameConfig!.blinds[roomState.value.gameConfig!.blinds.length - 1]
)
const form = ref({
  name: nickname.value ?? '',
  stack: biggestBlind.value * STACK_LIMITS.default,
  avatarId: Number(avatarId.value) || 1
})

const nameSchema = zod
  .string()
  .min(1, { message: t('Compete.message.NicknameRequired') })
  .min(2, { message: t('Compete.message.NicknameMinLength') })
  .max(14, { message: t('Compete.message.NicknameMaxLength') })

const stackSchema = zod
  .number({
    required_error: t('Compete.message.StackRequired'),
    invalid_type_error: 'Stack must be a number'
  })
  .int({ message: 'Stack must be integer' })
  .refine((val) => val <= biggestBlind.value * STACK_LIMITS.max, {
    message: `Max buy-in should be ${STACK_LIMITS.max} BB/STR (${biggestBlind.value * STACK_LIMITS.max})`
  })
  .refine((val) => val >= biggestBlind.value * STACK_LIMITS.min, {
    message: `Min buy-in should be ${STACK_LIMITS.min} BB/STR (${biggestBlind.value * STACK_LIMITS.min})`
  })

const nameErrorMessage = computed(() => validate(nameSchema, form.value.name))
const stackErrorMessage = computed(() => validate(stackSchema, form.value.stack))
const isFormValid = computed(() => !nameErrorMessage.value && !stackErrorMessage.value)

function submitForm() {
  if (!isFormValid.value) {
    return
  }

  // TODO: fix avatarId type
  sitDown({
    ...form.value,
    avatarId: String(form.value.avatarId),
    seatNo: uiState.seatIndex ?? undefined
  })

  nickname.value = form.value.name
  avatarId.value = String(form.value.avatarId)

  uiState.seatIndex = null
  closeModal()
}

function closeModal() {
  uiState.modals.joinGame.close()
}
</script>

<template>
  <modal-wrapper>
    <ui-panel
      class="join-game__panel"
      :title="title"
      tag="form"
      @submit.prevent="submitForm"
      @close="closeModal"
    >
      <div class="join-game__welcome-text">{{ welcomeText }}</div>
      <label for="nickname" class="join-game__welcome-control">
        {{ $t('Compete.label.PickYourNickname') }}
        <ui-input
          autofocus
          autocomplete="off"
          v-model.trim="form.name"
          :error="nameErrorMessage"
          name="name"
          placeholder="Nickname"
        >
          <template #icon><player-icon /></template>
        </ui-input>
      </label>
      <label for="stack" class="join-game__welcome-control">
        {{ $t('Compete.label.YourStack') }}
        <ui-input
          autofocus
          autocomplete="off"
          v-model.number="form.stack!"
          :error="stackErrorMessage"
          mark-dirty
          name="stack"
          placeholder="0"
          inputmode="numeric"
          :mask="{
            mask: '9 99#',
            reversed: true,
            tokens: { '9': { pattern: /[0-9]/, repeated: true } }
          }"
        >
          <template #icon><player-icon /></template>
        </ui-input>
      </label>
      <div class="join-game__welcome-control">
        <div>{{ $t('Compete.label.SelectYourAvatar') }}</div>
        <ui-avatar-selector v-model="form.avatarId" class="join-game__avatars" />
      </div>
      <ui-button>{{ actionText }}</ui-button>
    </ui-panel>
  </modal-wrapper>
</template>

<style lang="scss">
.join-game {
  &__welcome-text {
    text-align: center;
    font-weight: 400;
  }

  &__avatars {
    width: 100%;
    margin: 0 4px;
  }

  &__welcome-control {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
